/**
 * <ProductImage />
 *
 * Wraps a product image with a **Parts Buoy branded placeholder** shown
 * whenever the source is missing or fails to load.  The placeholder is
 * an inline SVG (zero network cost, always crisp) in brand red/black/white.
 *
 * Usage:
 *   <ProductImage src={product.images[0]} alt={product.title} className="h-full w-full object-cover" />
 */
import * as React from "react";
import { resolveUploadUrl } from "./ImageUploader";
import { cn } from "../../lib/cn";

interface Props extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  /** Server-relative or absolute URL.  Falsy or broken -> placeholder. */
  src?: string | null;
  /** Extra classes for the placeholder wrapper (so it matches the real image). */
  placeholderClassName?: string;
  /**
   * Optional URL to show when src is absent or broken, before falling back to
   * the inline SVG.  Pass productPlaceholderUrl from LogoContext so Carlton's
   * uploaded logo appears as the no-image stand-in on product cards.
   */
  fallbackSrc?: string | null;
}

export function ProductImage({ src, alt, className, placeholderClassName, fallbackSrc, ...rest }: Props) {
  const [broken, setBroken] = React.useState(false);
  const [fallbackBroken, setFallbackBroken] = React.useState(false);
  const resolved = src ? resolveUploadUrl(src) : null;

  if (resolved && !broken) {
    return (
      <img
        {...rest}
        src={resolved}
        alt={alt}
        className={className}
        onError={() => setBroken(true)}
      />
    );
  }

  if (fallbackSrc && !fallbackBroken) {
    return (
      <img
        src={fallbackSrc}
        alt={alt}
        className={cn(className, placeholderClassName)}
        onError={() => setFallbackBroken(true)}
      />
    );
  }

  return <PartsBuoyPlaceholder className={cn(className, placeholderClassName)} />;
}

/**
 * Parts Buoy logo-mark placeholder -- single inline SVG, no network hit.
 */
export function PartsBuoyPlaceholder({ className }: { className?: string }) {
  return (
    <div
      role="img"
      aria-label="Parts Buoy — no image provided"
      className={cn(
        "flex items-center justify-center bg-surface-sunken",
        className,
      )}
    >
      <svg
        viewBox="0 0 240 180"
        xmlns="http://www.w3.org/2000/svg"
        className="w-4/5 max-w-[240px]"
        aria-hidden
      >
        {/* soft diagonal pattern */}
        <defs>
          <pattern id="pb-diag" width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
            <line x1="0" y1="0" x2="0" y2="14" stroke="#0A0A0B" strokeOpacity="0.04" strokeWidth="1" />
          </pattern>
          <linearGradient id="pb-buoy" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E11D2E" />
            <stop offset="100%" stopColor="#B91528" />
          </linearGradient>
        </defs>
        <rect width="240" height="180" fill="url(#pb-diag)" />
        {/* wave lines */}
        <path d="M0 128 Q 30 118 60 128 T 120 128 T 180 128 T 240 128 V180 H0 Z" fill="#0A0A0B" fillOpacity="0.06" />
        <path d="M0 142 Q 30 132 60 142 T 120 142 T 180 142 T 240 142 V180 H0 Z" fill="#0A0A0B" fillOpacity="0.08" />

        {/* buoy body */}
        <g transform="translate(120,78)">
          <circle cx="0" cy="18" r="32" fill="url(#pb-buoy)" />
          <rect x="-26" y="6" width="52" height="6" fill="#0A0A0B" />
          <rect x="-26" y="22" width="52" height="6" fill="#0A0A0B" />
          <path d="M-6 -30 L 6 -30 L 12 -14 L -12 -14 Z" fill="#0A0A0B" />
          <rect x="-2" y="-46" width="4" height="16" fill="#0A0A0B" />
          <circle cx="0" cy="-48" r="4" fill="#E11D2E" />
        </g>

        {/* wordmark */}
        <text
          x="120"
          y="162"
          textAnchor="middle"
          fontFamily="Fraunces, Georgia, serif"
          fontWeight="900"
          fontSize="16"
          letterSpacing="-0.5"
          fill="#0A0A0B"
        >
          Parts <tspan fill="#E11D2E">Buoy</tspan>
        </text>
      </svg>
    </div>
  );
}
